/**
 * VisitReminderWidget — kompaktes Dashboard-Widget für Vor-Ort-Termin-Erinnerungen.
 *
 * Zeigt drei Zähler — Zähleinheit ist (Kunde × Segment), nicht reiner Kunde
 * (siehe FK §3.13 Teil B / T11):
 *   - Rot   → überfällig (kein zukünftiger Termin + letzter Termin > Frequenz)
 *   - Orange → bald fällig (kein zukünftiger Termin + letzter Termin > Frequenz − 1 Monat)
 *   - Grün  → OK
 *
 * Ein Kunde mit zwei Segmenten und beide überfällig zählt zweimal in „Rot".
 *
 * Klick auf einen Zähler navigiert zu /visits?filter=<color>&onlyMine=<bool> —
 * die gewählte Sicht reist mit, damit die Seite dieselbe Menge zeigt, die
 * die Kachel gezählt hat.
 */
import { Badge, Box, Group, Loader, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useVisitReminders, type ReminderColor } from "../hooks/useVisits.js";

interface ColorConfig {
  color: string;
  labelKey: string;
  descriptionKey: string;
}

const COLOR_CONFIG: Record<ReminderColor, ColorConfig> = {
  red: {
    color: "red",
    labelKey: "pages.visitReminderWidget.red",
    descriptionKey: "pages.visitReminderWidget.redDescription",
  },
  orange: {
    color: "orange",
    labelKey: "pages.visitReminderWidget.orange",
    descriptionKey: "pages.visitReminderWidget.orangeDescription",
  },
  green: {
    color: "green",
    labelKey: "pages.visitReminderWidget.green",
    descriptionKey: "pages.visitReminderWidget.greenDescription",
  },
};

interface ReminderCounterProps {
  color: ReminderColor;
  count: number;
  onClick: () => void;
}

function ReminderCounter({ color, count, onClick }: ReminderCounterProps) {
  const { t } = useTranslation();
  const config = COLOR_CONFIG[color];
  return (
    <Paper
      p="sm"
      withBorder
      style={{ cursor: "pointer", flex: 1 }}
      onClick={onClick}
    >
      <Stack gap={4} align="center">
        <ThemeIcon color={config.color} variant="light" size="xl" radius="md">
          <Text fw={700} size="lg" c={config.color}>
            {count}
          </Text>
        </ThemeIcon>
        <Badge color={config.color} variant="light" size="sm">
          {t(config.labelKey)}
        </Badge>
        <Text size="xs" c="dimmed" ta="center">
          {t(config.descriptionKey)}
        </Text>
      </Stack>
    </Paper>
  );
}

export function VisitReminderWidget({ onlyMine }: { onlyMine: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useVisitReminders(onlyMine);

  function handleFilterClick(filter: ReminderColor) {
    void navigate({ to: "/visits", search: { filter, onlyMine } as never });
  }

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {t("pages.visitReminderWidget.loading")}
          </Text>
        </Stack>
      </Paper>
    );
  }

  if (isError || !data) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          {t("pages.visitReminderWidget.error")}
        </Text>
      </Paper>
    );
  }

  return (
    <Box>
      <Text fw={600} mb="sm">
        {t("pages.visitReminderWidget.title")}
      </Text>
      <Group grow gap="sm">
        <ReminderCounter
          color="red"
          count={data.red}
          onClick={() => handleFilterClick("red")}
        />
        <ReminderCounter
          color="orange"
          count={data.orange}
          onClick={() => handleFilterClick("orange")}
        />
        <ReminderCounter
          color="green"
          count={data.green}
          onClick={() => handleFilterClick("green")}
        />
      </Group>
    </Box>
  );
}
